import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { parseAddresses } from "@/lib/clientAnnouncement";
import { Loader2, Mail, Plus, Save, Undo2, X } from "lucide-react";

/** One To / CC / BCC list: input + Add, removable chips. Same look as the other templates' Recipients card. */
function AddressList({ id, label, hint, items, setItems, elsewhere, allowNames, color, placeholder, disabled }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState(null);

  const add = () => {
    const { added, invalid, duplicates, taken } = parseAddresses(input, {
      existing: items.map((r) => r.email), elsewhere, allowNames,
    });
    if (added.length) setItems([...items, ...added]);
    // Keep only the problem entries in the box, so they can be fixed rather than retyped.
    setInput(invalid.join(", "));
    const problems = [
      invalid.length && `Enter a valid email address${invalid.length > 1 ? "es" : ""}`,
      taken.length && `Already used: ${taken.join(", ")}`,
      duplicates && !added.length && !invalid.length && !taken.length && "Already in this list",
    ].filter(Boolean);
    setError(problems.length ? problems.join(" · ") : null);
  };

  return (
    <div>
      <label htmlFor={id} className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</label>
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
      <div className="flex gap-2 mt-1">
        <Input id={id} placeholder={placeholder} value={input} disabled={disabled}
          aria-invalid={Boolean(error)} className={error ? "border-red-500" : ""}
          onChange={(e) => { setInput(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid={`${id}-input`} />
        <Button type="button" variant="outline" onClick={add} disabled={disabled || !input.trim()}
          className="shrink-0" aria-label={`Add ${label} recipient`} data-testid={`${id}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {error && <p className="text-xs text-red-600 mt-1" role="alert">{error}</p>}
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${id}-list`}>
          {items.map((r) => (
            <Badge key={r._key} variant="secondary" className={`${color} text-xs max-w-full`}>
              <Mail className="w-3 h-3 mr-1 shrink-0" />
              <span className="break-all">{r.name ? `${r.name} <${r.email}>` : r.email}</span>
              {!disabled && (
                <button type="button" onClick={() => setItems(items.filter((x) => x._key !== r._key))}
                  className="ml-1 hover:text-red-600" aria-label={`Remove ${r.email}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Each To address receives its own email (clients never see each other); CC and BCC
 * are copied on every one of those emails. `save` (null = no permission / still
 * loading) drives Save Recipients: { dirty, saving, hasSaved, onSave, onDiscard }.
 */
export default function RecipientFields({ to, setTo, cc, setCc, bcc, setBcc, limits, disabled, save }) {
  const owners = (lists) => Object.fromEntries(lists.flatMap(([label, items]) => items.map((r) => [r.email.toLowerCase(), label])));

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg">Recipients</CardTitle>
          {save?.dirty && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Unsaved changes</Badge>}
        </div>
        <p className="text-xs text-gray-500">
          Each To address receives its own email, so recipients never see each other. CC and BCC are copied on every
          email. Save Recipients to have these lists filled in next time.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <AddressList id="ca-to" label="To" items={to} setItems={setTo} allowNames
          hint={`Up to ${limits.max_recipients}. Use Name <email> to set the greeting name.`}
          placeholder="Add To email" elsewhere={owners([["CC", cc], ["BCC", bcc]])}
          color="bg-teal-50 text-teal-700" disabled={disabled} />
        <AddressList id="ca-cc" label="CC" items={cc} setItems={setCc}
          placeholder="Add CC email" elsewhere={owners([["To", to], ["BCC", bcc]])}
          color="bg-blue-50 text-blue-700" disabled={disabled} />
        <AddressList id="ca-bcc" label="BCC" items={bcc} setItems={setBcc}
          hint="BCC recipients are not visible to anyone else."
          placeholder="Add BCC email" elsewhere={owners([["To", to], ["CC", cc]])}
          color="bg-gray-100 text-gray-700" disabled={disabled} />
        {save && (
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            {save.dirty && save.hasSaved && (
              <Button type="button" variant="outline" onClick={save.onDiscard} disabled={save.saving || disabled}
                data-testid="ca-discard-recipients">
                <Undo2 className="w-4 h-4 mr-1" />Discard
              </Button>
            )}
            <Button type="button" className="bg-teal-600 hover:bg-teal-700"
              onClick={save.onSave} disabled={save.saving || disabled || !save.dirty}
              data-testid="ca-save-recipients">
              {save.saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save Recipients
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
